import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";

/**
 * Footer link columns mirroring the current homepage footer. URLs are absolute
 * and `/home`-prefixed so they resolve under the page's `<base href="/">`.
 *
 * @type {{ title: string, links: { label: string, href: string }[] }[]}
 */
const linkSections = [
  {
    title: "Product",
    links: [
      { label: "Sweepstakes", href: "/home/sweepstakes" },
      { label: "Network", href: "/home/network" },
      { label: "Apply Now", href: "/app/apply" },
    ],
  },
  {
    title: "About",
    links: [
      { label: "Pricing", href: "/home/pricing" },
      { label: "About Us", href: "/app/about-us" },
      { label: "Terms", href: "https://www.dojomojo.com/home/terms-of-use" },
      { label: "Privacy", href: "/home/privacy" },
      { label: "Contact Us", href: "mailto:hello@dojomojo.com" },
    ],
  },
  {
    title: "Resources",
    links: [{ label: "Guides", href: "/home/resources/all" }],
  },
  {
    title: "Help",
    links: [
      { label: "Knowledge Base", href: "https://help.dojomojo.com/hc/en-us" },
      { label: "Support", href: "mailto:hello@dojomojo.com" },
    ],
  },
];

/** @type {{ label: string, href: string, Icon: typeof FacebookIcon }[]} */
const socials = [
  { label: "Facebook", href: "https://www.facebook.com/dojomojoteam", Icon: FacebookIcon },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/dojomojo/mycompany/",
    Icon: LinkedInIcon,
  },
  { label: "X", href: "https://x.com/DojoMojoTeam", Icon: XIcon },
  { label: "Instagram", href: "https://www.instagram.com/dojomojoninja/", Icon: InstagramIcon },
];

/**
 * Site footer for the home v2 page: product/about/resources/help link columns,
 * contact email and social icons.
 *
 * @returns {import("react").ReactElement}
 */
export function HomeV2Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-line border-t bg-white px-6 py-12">
      <div className="mx-auto max-w-[1240px]">
        <div className="flex flex-col gap-10 md:flex-row md:justify-between">
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4 md:gap-x-16">
            {linkSections.map((section) => (
              <div key={section.title} className="flex flex-col gap-3">
                <span className="text-ink text-sm font-bold">{section.title}</span>
                {section.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-muted hover:text-cerulean text-sm transition"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 md:items-end">
            <a
              href="mailto:hello@dojomojo.com"
              className="text-muted hover:text-cerulean text-sm transition"
            >
              hello@dojomojo.com
            </a>
            <div className="flex items-center gap-2">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ink-soft hover:text-cerulean hover:bg-ice inline-flex h-9 w-9 items-center justify-center rounded-full transition"
                >
                  <Icon fontSize="small" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-line text-muted mt-10 flex items-center gap-2 border-t pt-6 text-sm">
          <span className="bg-grad-blue inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold text-white">
            DM
          </span>
          <span>© {year} DojoMojo. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}
